import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Thermometer, Check, Info } from "lucide-react";

const TempWidget: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const tabs = t("tabs", { returnObjects: true }) as any[];
    const tempTab = tabs.find((tab) => tab.id === "temp");
    const widget = tempTab?.widget || {};

    return (
        <motion.div
            className="relative w-full"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
        >
            {/* Main widget */}
            <motion.div
                className="bg-gradient-to-br from-amber-50 via-white to-amber-50 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-amber-100 mb-3 overflow-hidden relative"
                whileHover={{ boxShadow: "0 10px 25px -5px rgba(251, 191, 36, 0.2)" }}
                transition={{ duration: 0.3 }}
            >
                {/* Background effects */}
                <div className={`absolute ${isRTL ? "left-10" : "right-10"} -top-10 w-40 h-40 bg-gradient-to-br from-amber-200 to-amber-100 rounded-full opacity-20 blur-2xl`}></div>
                <div className={`absolute ${isRTL ? "right-10" : "left-10"} -bottom-10 w-40 h-40 bg-gradient-to-br from-orange-200 to-amber-100 rounded-full opacity-20 blur-2xl`}></div>

                <div className="flex justify-between items-center mb-3 relative z-10">
                    <h5 className="text-amber-800 font-medium flex items-center">
                        <Thermometer className={`w-5 h-5 ${isRTL ? "ml-1" : "mr-1"} text-amber-600`} />
                        {widget.title}
                    </h5>
                    <motion.span
                        className="text-xs bg-gradient-to-r from-amber-500 to-amber-600 text-white px-3 py-1 rounded-full font-medium"
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.4 }}
                    >
                        {widget.value}
                    </motion.span>
                </div>

                {/* Temperature display */}
                <div className="relative h-36 mb-4 overflow-hidden rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 flex items-center justify-center border border-amber-100/50 shadow-inner">
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-400/10 to-orange-500/10"></div>

                    {/* Thermometer animation */}
                    <div className="relative flex items-center">
                        <div className="relative">
                            <motion.div
                                className={`relative w-8 h-28 me-2 bg-white rounded-full overflow-hidden border-2 border-amber-300 flex justify-center shadow-md`}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7 }}
                            >
                                <motion.div
                                    className="absolute bottom-0 w-5 rounded-full"
                                    style={{ background: "linear-gradient(to top, #ef4444, #f59e0b, #f59e0b)" }}
                                    initial={{ height: "0%" }}
                                    animate={{ height: "70%" }}
                                    transition={{ duration: 1.8, delay: 0.5, ease: "easeInOut" }}
                                >
                                    {/* Shimmer effect */}
                                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-4 h-8 bg-white/30 rounded-full blur-sm"></div>
                                </motion.div>

                                {/* Scale lines */}
                                <div className={`absolute ${isRTL ? "left-0" : "right-0"} inset-y-0 w-2 flex flex-col justify-between py-2`}>
                                    {[...Array(8)].map((_, i) => (
                                        <div key={i} className="h-px w-2 bg-gray-300"></div>
                                    ))}
                                </div>

                                <div className="absolute inset-0 flex flex-col justify-between py-2 px-1">
                                    <span className="text-[8px] text-gray-400">38°</span>
                                    <span className="text-[8px] text-gray-400">37°</span>
                                    <motion.span className="text-[9px] font-bold text-amber-600" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.3, duration: 0.5 }}>
                                        36.5°
                                    </motion.span>
                                    <span className="text-[8px] text-gray-400">36°</span>
                                    <span className="text-[8px] text-gray-400">35°</span>
                                </div>
                            </motion.div>

                            {/* Glow effect */}
                            <motion.div
                                className="absolute inset-0 rounded-full bg-amber-400/30 blur-md -z-10"
                                animate={{ opacity: [0.2, 0.4, 0.2], scale: [0.9, 1.05, 0.9] }}
                                transition={{ duration: 3, repeat: Infinity, repeatType: "reverse" }}
                            />
                        </div>

                        <motion.div
                            className={`${isRTL ? "mr-6" : "ml-6"} flex flex-col items-center`}
                            initial={{ opacity: 0, x: 15 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.7 }}
                        >
                            <span className="text-3xl font-bold bg-gradient-to-r from-amber-500 to-amber-700 bg-clip-text text-transparent">{widget.value}</span>
                            <div className="flex items-center mt-1">
                                <motion.span
                                    className="text-xs text-green-600 font-medium flex items-center bg-green-50 px-2 py-0.5 rounded-full"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 1.2 }}
                                >
                                    {widget.normalTemp}
                                    <Check className={`w-3 h-3 ${isRTL ? "ml-1" : "mr-1"} mt-0.5 text-green-600`} />
                                </motion.span>
                            </div>
                        </motion.div>
                    </div>

                    {/* Recording indicator */}
                    <motion.div className={`absolute top-2 ${isRTL ? "right-2" : "left-2"} flex items-center`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
                        <motion.div
                            className={`w-2 h-2 rounded-full bg-green-500 ${isRTL ? "mr-1" : "ml-1"}`}
                            animate={{ opacity: [1, 0.3, 1], scale: [1, 0.8, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        />
                        <span className="text-[9px] text-gray-500">{widget.recording}</span>
                    </motion.div>
                </div>

                {/* Status and comparison */}
                <div className="flex justify-between items-center mb-2 relative z-10">
                    <div className="flex items-center">
                        <span className="text-sm text-gray-600 ml-1">{widget.statusLabel}:</span>
                        <motion.span className="text-sm font-medium text-green-600 flex items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
                            {widget.status}
                            <Check className={`w-4 h-4 ${isRTL ? "ml-1" : "mr-1"} text-green-600`} />
                        </motion.span>
                    </div>
                    <motion.div className="text-xs text-gray-500 flex items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
                        <span className="text-amber-600 ml-1.5">{widget.comparison?.value}</span>
                        <span>{widget.comparison?.label}</span>
                    </motion.div>
                </div>
            </motion.div>

            {/* Sub-widgets */}
            <div className="grid grid-cols-2 gap-3 mb-3">
                {/* 24h trend */}
                <motion.div
                    className="bg-gradient-to-br from-white to-amber-50 backdrop-blur-sm rounded-xl p-3 shadow-sm border border-amber-100 relative overflow-hidden"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    whileHover={{ boxShadow: "0 8px 20px -5px rgba(251, 191, 36, 0.15)", y: -2 }}
                >
                    <div className={`absolute ${isRTL ? "right-6" : "left-6"} -bottom-6 w-20 h-20 bg-amber-200/20 rounded-full blur-xl`}></div>

                    <div className="flex items-center justify-between mb-2 relative z-10">
                        <span className="text-xs text-gray-600 font-medium">{widget.trend24h}</span>
                    </div>

                    {/* Mini trend chart */}
                    <div className="relative h-16">
                        <svg className="w-full h-full" viewBox="0 0 100 30" preserveAspectRatio="none">
                            <defs>
                                <linearGradient id="temp-chart-gradient" x1="0" y1="0" x2="0" y2="40">
                                    <stop offset="0%" stopColor="#F59E0B" />
                                    <stop offset="100%" stopColor="#F59E0B" stopOpacity="0" />
                                </linearGradient>
                                <linearGradient id="temp-line-gradient" x1="0" y1="0" x2="100" y2="0">
                                    <stop offset="0%" stopColor="#F59E0B" />
                                    <stop offset="100%" stopColor="#FB923C" />
                                </linearGradient>
                            </defs>

                            {/* Area under curve */}
                            <motion.path
                                d="M 0,30 Q 10,28 20,30 T 40,32 T 60,29 T 80,30 T 100,28 V 40 H 0 Z"
                                fill="url(#temp-chart-gradient)"
                                fillOpacity="0.3"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 1.5, delay: 0.5 }}
                            />

                            {/* Line */}
                            <motion.path
                                d="M 0,30 Q 10,28 20,30 T 40,32 T 60,29 T 80,30 T 100,28"
                                fill="none"
                                stroke="url(#temp-line-gradient)"
                                strokeWidth="2"
                                strokeLinecap="round"
                                initial={{ pathLength: 0, opacity: 0 }}
                                animate={{ pathLength: 1, opacity: 1 }}
                                transition={{ duration: 1.8, delay: 0.3 }}
                            />

                            {/* Data points */}
                            {[
                                { x: 0, y: 30 },
                                { x: 20, y: 30 },
                                { x: 40, y: 32 },
                                { x: 60, y: 29 },
                                { x: 80, y: 30 },
                                { x: 100, y: 28 },
                            ].map((point, i) => (
                                <motion.circle
                                    key={i}
                                    cx={point.x}
                                    cy={point.y}
                                    r="2"
                                    fill="white"
                                    stroke="#F59E0B"
                                    strokeWidth="1"
                                    initial={{ opacity: 0, scale: 0 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.8 + i * 0.15, duration: 0.5 }}
                                />
                            ))}

                            {/* Moving point */}
                            <motion.circle
                                cx="60"
                                cy="29"
                                r="3"
                                fill="#F59E0B"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: [0, 1, 0], scale: [0.5, 1.5, 0.5], cx: [60, 80, 100] }}
                                transition={{ duration: 4, delay: 2, repeat: Infinity, repeatType: "loop", ease: "easeInOut" }}
                            />

                            {/* Reference line */}
                            <line x1="0" y1="30" x2="100" y2="30" stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="2,2" />
                        </svg>

                        {/* Time labels */}
                        <div className="absolute bottom-0 w-full flex justify-between text-[8px] text-gray-500">
                            <span className="bg-white/50 px-1 rounded">{widget.timeLabels?.morning}</span>
                            <span className="bg-white/50 px-1 rounded">{widget.timeLabels?.noon}</span>
                            <span className="bg-white/50 px-1 rounded">{widget.timeLabels?.night}</span>
                        </div>
                    </div>
                </motion.div>

                {/* Temperature range */}
                <motion.div
                    className="bg-gradient-to-br from-white to-amber-50 backdrop-blur-sm rounded-xl p-3 shadow-sm border border-amber-100 relative overflow-hidden"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    whileHover={{ boxShadow: "0 8px 20px -5px rgba(251, 191, 36, 0.15)", y: -2 }}
                >
                    <div className={`absolute ${isRTL ? "left-6" : "right-6"} -bottom-6 w-20 h-20 bg-amber-200/20 rounded-full blur-xl`}></div>

                    <div className="flex items-center justify-between mb-2 relative z-10">
                        <span className="text-xs text-gray-600 font-medium">{widget.rangeToday}</span>
                    </div>

                    {/* Temperature range display */}
                    <div className="relative h-16 flex flex-col justify-center">
                        <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden shadow-inner">
                            <div className="relative w-full h-full">
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-300 via-green-300 to-red-300"></div>

                                {/* Normal range */}
                                <motion.div
                                    className="absolute top-0 bottom-0 bg-white/30 border border-green-300"
                                    style={{ left: "40%", width: "30%" }}
                                    initial={{ opacity: 0, width: "0%" }}
                                    animate={{ opacity: 1, width: "30%" }}
                                    transition={{ duration: 1, delay: 0.8 }}
                                ></motion.div>

                                {/* Current temp indicator */}
                                <motion.div
                                    className="absolute top-0 bottom-0 w-3 bg-white shadow-md border-2 border-amber-500 rounded-full"
                                    style={{ left: "60%" }}
                                    initial={{ left: "0%" }}
                                    animate={{ left: "60%" }}
                                    transition={{ duration: 1.5, delay: 0.7, ease: "easeOut" }}
                                >
                                    {/* Glow effect */}
                                    <motion.div
                                        className="absolute inset-0 rounded-full bg-amber-400 blur-md -z-10"
                                        animate={{ opacity: [0.4, 0.8, 0.4], scale: [0.8, 1.5, 0.8] }}
                                        transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
                                    />
                                </motion.div>
                            </div>
                        </div>

                        {/* Temperature labels */}
                        <div className="flex justify-between text-[8px] text-gray-500 mt-2">
                            {widget.tempLabels?.map((label: string, index: number) => (
                                <span key={index} className="bg-white/50 px-1 rounded">
                                    {label}
                                </span>
                            ))}
                        </div>

                        {/* Normal range label */}
                        <motion.div
                            className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-3 bg-green-100 text-green-700 text-[8px] px-1.5 py-0.5 rounded"
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.2 }}
                        >
                            {widget.normalRange}
                        </motion.div>
                    </div>
                </motion.div>
            </div>

            {/* Tips */}
            <motion.div
                className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-3 text-sm text-amber-800 border border-amber-100 shadow-sm relative overflow-hidden"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                whileHover={{ boxShadow: "0 8px 20px -5px rgba(251, 191, 36, 0.15)" }}
            >
                <div className={`absolute ${isRTL ? "right-10" : "left-10"} -bottom-10 w-40 h-40 bg-amber-200/20 rounded-full blur-2xl`}></div>

                <div className="flex items-start mb-2 relative z-10">
                    <Info className={`w-4 h-4 ${isRTL ? "mr-1.5" : "ml-1.5"} mt-1 text-amber-600`} />
                    <h6 className="font-medium text-amber-800">{widget.tips?.title}</h6>
                </div>

                <div className="space-y-2 relative z-10">
                    {widget.tips?.items?.map((item: string, index: number) => (
                        <motion.p key={index} className="flex items-start text-xs" initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.1 + index * 0.2 }}>
                            <Check className={`w-3 h-3 text-amber-500 mt-0.5 ${isRTL ? "mr-1" : "ml-1"} flex-shrink-0`} />
                            {item}
                        </motion.p>
                    ))}
                </div>
            </motion.div>
        </motion.div>
    );
};

export default TempWidget;
